import type {
	Cuisine,
	PrimaryCarb,
	PrimaryProtein,
	SauceBase,
} from '@hewey/shared'
import { FilterBar, type FilterFacet } from '@components/FilterBar.tsx'
import {
	cleanCarb,
	cleanCuisine,
	cleanProtein,
	cleanSauce,
} from './tagSummary.ts'
import {
	activeFilterCount,
	DEFAULT_RECIPE_FILTER,
	type RecipeFilter,
	TIME_BUCKET_LABEL,
	TIME_BUCKETS,
	type TimeBucket,
} from './filter.ts'

type Props = {
	filter: RecipeFilter
	setFilter: (next: RecipeFilter) => void
}

// Clean label maps already drop the noise values (unknown/other/none), so
// their entries are exactly the filterable options, in enum order.
const optionsFrom = (
	map: Partial<Record<string, string>>,
): { value: string; label: string }[] =>
	Object.entries(map).map(([value, label]) => ({
		value,
		label: label ?? value,
	}))

const toggle = <T,>(arr: readonly T[], v: T): T[] =>
	arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]

export const RecipeFilterBar = ({ filter, setFilter }: Props) => {
	const facets: FilterFacet[] = [
		{
			key: 'cuisine',
			label: 'Cuisine',
			options: optionsFrom(cleanCuisine),
			selected: filter.cuisines,
			onToggle: (v) =>
				setFilter({
					...filter,
					cuisines: toggle(filter.cuisines, v as Cuisine),
				}),
			onClear: () => setFilter({ ...filter, cuisines: [] }),
		},
		{
			key: 'protein',
			label: 'Protein',
			options: optionsFrom(cleanProtein),
			selected: filter.proteins,
			onToggle: (v) =>
				setFilter({
					...filter,
					proteins: toggle(filter.proteins, v as PrimaryProtein),
				}),
			onClear: () => setFilter({ ...filter, proteins: [] }),
		},
		{
			key: 'carb',
			label: 'Carb',
			options: optionsFrom(cleanCarb),
			selected: filter.carbs,
			onToggle: (v) =>
				setFilter({
					...filter,
					carbs: toggle(filter.carbs, v as PrimaryCarb),
				}),
			onClear: () => setFilter({ ...filter, carbs: [] }),
		},
		{
			key: 'sauce',
			label: 'Sauce',
			options: optionsFrom(cleanSauce),
			selected: filter.sauces,
			onToggle: (v) =>
				setFilter({
					...filter,
					sauces: toggle(filter.sauces, v as SauceBase),
				}),
			onClear: () => setFilter({ ...filter, sauces: [] }),
		},
		{
			key: 'time',
			label: 'Time',
			options: TIME_BUCKETS.map((value) => ({
				value,
				label: TIME_BUCKET_LABEL[value],
			})),
			selected: filter.times,
			onToggle: (v) =>
				setFilter({
					...filter,
					times: toggle(filter.times, v as TimeBucket),
				}),
			onClear: () => setFilter({ ...filter, times: [] }),
		},
	]

	return (
		<FilterBar
			facets={facets}
			activeCount={activeFilterCount(filter)}
			onClearAll={() => setFilter(DEFAULT_RECIPE_FILTER)}
		/>
	)
}
